<!-- ============================================================
 * PlatformInquiryDrawer.vue
 * 레코스 플랫폼 사용 문의 드로어 — 화면 오른쪽에서 슬라이드 (§3.3)
 *
 * Props:
 *   isOpen (Boolean) — 열림/닫힘 제어 (부모에서 관리)
 *
 * Events:
 *   close                — 닫기 요청
 *   submitted (message)  — 접수 완료 (서버 응답 메시지를 부모로 전달)
 *
 * 역할 전환(발행자↔수령자) 시 기존 입력값·파일은 DOM에 유지(v-show),
 * 제출 시 활성화된 역할의 폼 데이터만 서버로 전송 (§3.3)
 * ============================================================ -->
<template>
    <div>
        <!-- 배경 오버레이 -->
        <transition name="pid-bg">
            <div
                v-if="isOpen"
                class="pid-backdrop"
                aria-hidden="true"
                @click="handleClose"
            />
        </transition>

        <!-- 드로어 패널 (오른쪽) -->
        <aside
            class="pid-panel"
            :class="{ 'pid-panel--open': isOpen }"
            role="dialog"
            aria-modal="true"
            aria-label="플랫폼 사용 문의 폼"
        >
            <!-- ── 헤더 ───────────────────────────────────── -->
            <div class="pid-hd">
                <div class="pid-hd__info">
                    <h2 class="pid-hd__title">플랫폼 사용 문의</h2>
                    <p class="pid-hd__desc">안녕하세요 😊 레코스 플랫폼 이용 중 궁금하신 내용을 남겨주시면 확인 후 순차적으로 메일 회신드리겠습니다.</p>
                </div>
                <button type="button" class="pid-close-btn" aria-label="닫기" @click="handleClose">
                    <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                        <path d="M2 2l14 14M16 2L2 16" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/>
                    </svg>
                </button>
            </div>

            <!-- ── 바디 (스크롤 가능) ─────────────────────── -->
            <div ref="scrollBody" class="pid-body">

                <!-- 허니팟(스팸 봇 차단): 사용자 눈에 안 보이는 가짜 필드. 값이 채워지면 서버가 봇으로 간주해 무시한다.
                     name은 브라우저 자동완성이 인식하지 못하도록 비표준 토큰을 쓴다(website/url/email 등 금지) — 정상 유저 오탐 방지. -->
                <input
                    v-model="website"
                    type="text"
                    name="contact_extra"
                    tabindex="-1"
                    autocomplete="off"
                    aria-hidden="true"
                    style="position:absolute; left:-9999px; width:1px; height:1px; opacity:0;"
                />

                <!-- ════════════════════════════════════
                 역할 선택 (발행자 / 수령자)
                ════════════════════════════════════ -->
                <section class="pid-sec">
                    <h3 class="pid-sec__title">
                        문의 대상
                        <span class="pid-badge pid-badge--req">필수</span>
                    </h3>
                    <div class="pid-role-wrap" :class="{ 'pid-role-wrap--err': errors.role }">
                        <!-- 발행자 -->
                        <button
                            type="button"
                            class="pid-role-btn"
                            :class="{ 'pid-role-btn--on': selectedRole === 'ISSUER' }"
                            @click="selectRole('ISSUER')"
                        >
                            <span class="pid-role-btn__icon" aria-hidden="true">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <rect x="2" y="7" width="20" height="14" rx="2" stroke="currentColor" stroke-width="1.5"/>
                                    <path d="M16 7V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v2" stroke="currentColor" stroke-width="1.5"/>
                                </svg>
                            </span>
                            <span class="pid-role-btn__body">
                                <span class="pid-role-btn__main">발행자</span>
                                <span class="pid-role-btn__sub">배지를 발급하는 기관 담당자</span>
                            </span>
                        </button>
                        <!-- 수령자 -->
                        <button
                            type="button"
                            class="pid-role-btn"
                            :class="{ 'pid-role-btn--on': selectedRole === 'RECEIVER' }"
                            @click="selectRole('RECEIVER')"
                        >
                            <span class="pid-role-btn__icon" aria-hidden="true">
                                <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                                    <circle cx="12" cy="8" r="4" stroke="currentColor" stroke-width="1.5"/>
                                    <path d="M4 20c0-3.314 3.582-6 8-6s8 2.686 8 6" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
                                </svg>
                            </span>
                            <span class="pid-role-btn__body">
                                <span class="pid-role-btn__main">수령자</span>
                                <span class="pid-role-btn__sub">배지를 수령하는 개인</span>
                            </span>
                        </button>
                    </div>
                    <span v-if="errors.role" class="pid-errmsg">{{ errors.role }}</span>
                </section>

                <!-- ════════════════════════════════════
                 발행자 폼 (v-show — DOM 유지, 역할 전환 시 입력값 보존)
                ════════════════════════════════════ -->
                <div v-show="selectedRole === 'ISSUER'" :aria-hidden="selectedRole !== 'ISSUER'">
                    <section class="pid-sec">
                        <h3 class="pid-sec__title">
                            기본 정보
                            <span class="pid-badge pid-badge--req">필수</span>
                        </h3>

                        <!-- 회사명(발행처명) · 이름 -->
                        <div class="pid-grid">
                            <div class="pid-field" :class="{ 'pid-field--err': errors.issuer_companyName }">
                                <label class="pid-lbl">회사명 (발행처명)</label>
                                <input
                                    v-model.trim="issuerForm.companyName"
                                    maxlength="200"
                                    class="pid-inp"
                                    type="text"
                                    placeholder="발행 기관명"
                                    :disabled="selectedRole !== 'ISSUER'"
                                    @input="clearErr('issuer_companyName')"
                                />
                                <span v-if="errors.issuer_companyName" class="pid-errmsg">{{ errors.issuer_companyName }}</span>
                            </div>
                            <div class="pid-field" :class="{ 'pid-field--err': errors.issuer_contactName }">
                                <label class="pid-lbl">이름</label>
                                <input
                                    v-model.trim="issuerForm.contactName"
                                    maxlength="100"
                                    class="pid-inp"
                                    type="text"
                                    placeholder="담당자 이름"
                                    :disabled="selectedRole !== 'ISSUER'"
                                    @input="clearErr('issuer_contactName')"
                                />
                                <span v-if="errors.issuer_contactName" class="pid-errmsg">{{ errors.issuer_contactName }}</span>
                            </div>
                            <!-- 연락처 -->
                            <div class="pid-field" :class="{ 'pid-field--err': errors.issuer_phone }">
                                <label class="pid-lbl">연락처</label>
                                <input
                                    v-model.trim="issuerForm.phone"
                                    maxlength="50"
                                    class="pid-inp"
                                    type="tel"
                                    placeholder="연락 가능한 연락처를 작성해주세요."
                                    :disabled="selectedRole !== 'ISSUER'"
                                    @input="clearErr('issuer_phone')"
                                    @blur="issuerForm.phone = formatPhoneKR(issuerForm.phone)"
                                />
                                <span v-if="errors.issuer_phone" class="pid-errmsg">{{ errors.issuer_phone }}</span>
                            </div>
                            <!-- 이메일 -->
                            <div class="pid-field" :class="{ 'pid-field--err': errors.issuer_email }">
                                <label class="pid-lbl">이메일</label>
                                <input
                                    v-model.trim="issuerForm.email"
                                    maxlength="300"
                                    class="pid-inp"
                                    type="email"
                                    placeholder="example@company.com"
                                    :disabled="selectedRole !== 'ISSUER'"
                                    @input="clearErr('issuer_email')"
                                />
                                <span v-if="errors.issuer_email" class="pid-errmsg">{{ errors.issuer_email }}</span>
                            </div>
                        </div>
                    </section>

                    <!-- 발행자 — 문의 내용 -->
                    <section class="pid-sec">
                        <h3 class="pid-sec__title">
                            문의 내용
                            <span class="pid-badge pid-badge--opt">선택</span>
                        </h3>

                        <div class="pid-field">
                            <label class="pid-lbl">상세 내용</label>
                            <textarea
                                v-model="issuerForm.description"
                                maxlength="4000"
                                class="pid-inp pid-inp--ta"
                                rows="4"
                                placeholder="문의하실 내용을 자유롭게 입력해 주세요."
                                :disabled="selectedRole !== 'ISSUER'"
                            />
                        </div>

                        <!-- 파일 업로드 -->
                        <div class="pid-field">
                            <label class="pid-lbl">첨부파일</label>
                            <contact-file-upload
                                ref="issuerUpload"
                                v-model="issuerFiles"
                            />
                        </div>
                    </section>

                    <!-- 발행자 — 개인정보 동의 -->
                    <section class="pid-sec pid-sec--last">
                        <div class="pid-field" :class="{ 'pid-field--err': errors.issuer_agree }">
                            <div class="pid-agree">
                                <button
                                    type="button"
                                    class="pid-agree__box"
                                    :class="{ 'pid-agree__box--on': issuerForm.agreePrivacy }"
                                    aria-label="개인정보 수집 및 이용 동의"
                                    :disabled="selectedRole !== 'ISSUER'"
                                    @click="issuerForm.agreePrivacy = !issuerForm.agreePrivacy; clearErr('issuer_agree')"
                                >
                                    <svg v-if="issuerForm.agreePrivacy" width="12" height="10" viewBox="0 0 12 10" fill="none">
                                        <path d="M1 5L4.5 8.5L11 1" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                                <p class="pid-agree__txt">
                                    <a :href="privacyHref" target="_blank" rel="noopener noreferrer" class="pid-agree__link">개인정보 수집 및 이용</a>에
                                    동의합니다. <span class="pid-badge pid-badge--req">필수</span>
                                </p>
                            </div>
                            <span v-if="errors.issuer_agree" class="pid-errmsg">{{ errors.issuer_agree }}</span>
                        </div>
                    </section>
                </div>

                <!-- ════════════════════════════════════
                 수령자 폼 (v-show — DOM 유지, 역할 전환 시 입력값 보존)
                ════════════════════════════════════ -->
                <div v-show="selectedRole === 'RECEIVER'" :aria-hidden="selectedRole !== 'RECEIVER'">
                    <section class="pid-sec">
                        <h3 class="pid-sec__title">
                            기본 정보
                            <span class="pid-badge pid-badge--req">필수</span>
                        </h3>

                        <div class="pid-grid">
                            <!-- 이름 -->
                            <div class="pid-field" :class="{ 'pid-field--err': errors.rcv_contactName }">
                                <label class="pid-lbl">이름</label>
                                <input
                                    v-model.trim="receiverForm.contactName"
                                    maxlength="100"
                                    class="pid-inp"
                                    type="text"
                                    placeholder="이름"
                                    :disabled="selectedRole !== 'RECEIVER'"
                                    @input="clearErr('rcv_contactName')"
                                />
                                <span v-if="errors.rcv_contactName" class="pid-errmsg">{{ errors.rcv_contactName }}</span>
                            </div>
                            <!-- 연락처 -->
                            <div class="pid-field" :class="{ 'pid-field--err': errors.rcv_phone }">
                                <label class="pid-lbl">연락처</label>
                                <input
                                    v-model.trim="receiverForm.phone"
                                    maxlength="50"
                                    class="pid-inp"
                                    type="tel"
                                    placeholder="연락 가능한 연락처를 작성해주세요."
                                    :disabled="selectedRole !== 'RECEIVER'"
                                    @input="clearErr('rcv_phone')"
                                    @blur="receiverForm.phone = formatPhoneKR(receiverForm.phone)"
                                />
                                <span v-if="errors.rcv_phone" class="pid-errmsg">{{ errors.rcv_phone }}</span>
                            </div>
                            <!-- 이메일 -->
                            <div class="pid-field" :class="{ 'pid-field--err': errors.rcv_email }">
                                <label class="pid-lbl">이메일</label>
                                <input
                                    v-model.trim="receiverForm.email"
                                    maxlength="300"
                                    class="pid-inp"
                                    type="email"
                                    placeholder="example@email.com"
                                    :disabled="selectedRole !== 'RECEIVER'"
                                    @input="clearErr('rcv_email')"
                                />
                                <span v-if="errors.rcv_email" class="pid-errmsg">{{ errors.rcv_email }}</span>
                            </div>
                            <!-- 발행처 -->
                            <div class="pid-field" :class="{ 'pid-field--err': errors.rcv_publisherName }">
                                <label class="pid-lbl">발행처</label>
                                <input
                                    v-model.trim="receiverForm.publisherName"
                                    maxlength="200"
                                    class="pid-inp"
                                    type="text"
                                    placeholder="배지를 발급한 기관명"
                                    :disabled="selectedRole !== 'RECEIVER'"
                                    @input="clearErr('rcv_publisherName')"
                                />
                                <span v-if="errors.rcv_publisherName" class="pid-errmsg">{{ errors.rcv_publisherName }}</span>
                            </div>
                            <!-- 배지명 -->
                            <div class="pid-field" :class="{ 'pid-field--err': errors.rcv_badgeName }">
                                <label class="pid-lbl">배지명</label>
                                <input
                                    v-model.trim="receiverForm.badgeName"
                                    maxlength="300"
                                    class="pid-inp"
                                    type="text"
                                    placeholder="수령한 배지 이름"
                                    :disabled="selectedRole !== 'RECEIVER'"
                                    @input="clearErr('rcv_badgeName')"
                                />
                                <span v-if="errors.rcv_badgeName" class="pid-errmsg">{{ errors.rcv_badgeName }}</span>
                            </div>
                            <!-- 지갑 생성 여부 (필수) -->
                            <div class="pid-field" :class="{ 'pid-field--err': errors.rcv_walletStatus }">
                                <label class="pid-lbl">지갑 생성 여부</label>
                                <div class="pid-opt-wrap">
                                    <button
                                        v-for="opt in WALLET_OPTIONS"
                                        :key="opt.value"
                                        type="button"
                                        class="pid-opt pid-opt--radio"
                                        :class="{ 'pid-opt--on': receiverForm.walletStatus === opt.value }"
                                        :disabled="selectedRole !== 'RECEIVER'"
                                        @click="receiverForm.walletStatus = opt.value; clearErr('rcv_walletStatus')"
                                    >{{ opt.label }}</button>
                                </div>
                                <span v-if="errors.rcv_walletStatus" class="pid-errmsg">{{ errors.rcv_walletStatus }}</span>
                            </div>
                        </div>
                    </section>

                    <!-- 수령자 — 문의 내용 -->
                    <section class="pid-sec">
                        <h3 class="pid-sec__title">
                            문의 내용
                            <span class="pid-badge pid-badge--opt">선택</span>
                        </h3>

                        <div class="pid-field">
                            <label class="pid-lbl">상세 내용</label>
                            <textarea
                                v-model="receiverForm.description"
                                maxlength="4000"
                                class="pid-inp pid-inp--ta"
                                rows="4"
                                placeholder="문의하실 내용을 자유롭게 입력해 주세요."
                                :disabled="selectedRole !== 'RECEIVER'"
                            />
                        </div>

                        <!-- 파일 업로드 -->
                        <div class="pid-field">
                            <label class="pid-lbl">첨부파일</label>
                            <contact-file-upload
                                ref="receiverUpload"
                                v-model="receiverFiles"
                            />
                        </div>
                    </section>

                    <!-- 수령자 — 개인정보 동의 -->
                    <section class="pid-sec pid-sec--last">
                        <div class="pid-field" :class="{ 'pid-field--err': errors.rcv_agree }">
                            <div class="pid-agree">
                                <button
                                    type="button"
                                    class="pid-agree__box"
                                    :class="{ 'pid-agree__box--on': receiverForm.agreePrivacy }"
                                    aria-label="개인정보 수집 및 이용 동의"
                                    :disabled="selectedRole !== 'RECEIVER'"
                                    @click="receiverForm.agreePrivacy = !receiverForm.agreePrivacy; clearErr('rcv_agree')"
                                >
                                    <svg v-if="receiverForm.agreePrivacy" width="12" height="10" viewBox="0 0 12 10" fill="none">
                                        <path d="M1 5L4.5 8.5L11 1" stroke="white" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
                                    </svg>
                                </button>
                                <p class="pid-agree__txt">
                                    <a :href="privacyHref" target="_blank" rel="noopener noreferrer" class="pid-agree__link">개인정보 수집 및 이용</a>에
                                    동의합니다. <span class="pid-badge pid-badge--req">필수</span>
                                </p>
                            </div>
                            <span v-if="errors.rcv_agree" class="pid-errmsg">{{ errors.rcv_agree }}</span>
                        </div>
                    </section>
                </div>

                <!-- 역할 미선택 안내 -->
                <div v-if="!selectedRole" class="pid-role-hint">
                    <span class="pid-role-hint__ico" aria-hidden="true">↑</span>
                    <p class="pid-role-hint__txt">위에서 문의 대상을 선택하면 입력 폼이 표시됩니다.</p>
                </div>

            </div><!-- /pid-body -->

            <!-- ── 푸터 (제출 버튼 — 역할 선택 전 미노출, §3.3) ── -->
            <div class="pid-ft">
                <!-- 서버/네트워크 오류 안내 (§3.4 재시도 유도) -->
                <p v-if="selectedRole && submitError" class="pid-submit-err" role="alert">{{ submitError }}</p>
                <button
                    v-if="selectedRole"
                    type="button"
                    class="pid-submit"
                    :disabled="isSubmitting"
                    @click="handleSubmit"
                >
                    <span v-if="isSubmitting" class="pid-submit__spinner" aria-hidden="true" />
                    <span>{{ isSubmitting ? '제출 중…' : '문의 제출' }}</span>
                </button>
                <p v-else class="pid-ft__hint">발행자 또는 수령자를 먼저 선택해 주세요.</p>
            </div>
        </aside>
    </div>
</template>

<script>
import ContactFileUpload from './ContactFileUpload.vue';
import { resolveApiErrorMessage } from '@/utils/apiError';
import { formatPhoneKR } from '@/utils/phone';

// ── 지갑 생성 여부 선택지 (§3.3) ────────────────────────────
// 사용자 화면: '예' → DB: '생성완료', '아니요' → DB: '미생성'
const WALLET_OPTIONS = [
    { label: '예',    value: '생성완료' },
    { label: '아니요', value: '미생성'   },
];

/** 발행자 폼 초기 상태 */
function makeIssuerForm() {
    return {
        companyName:  '',
        contactName:  '',
        phone:        '',
        email:        '',
        description:  '',
        agreePrivacy: false,
    };
}

/** 수령자 폼 초기 상태 */
function makeReceiverForm() {
    return {
        contactName:   '',
        phone:         '',
        email:         '',
        publisherName: '',
        badgeName:     '',
        walletStatus:  null, // '생성완료' | '미생성' | null
        description:   '',
        agreePrivacy:  false,
    };
}

export default {
    name: 'PlatformInquiryDrawer',

    components: { ContactFileUpload },

    props: {
        isOpen: { type: Boolean, default: false },
    },

    data() {
        return {
            WALLET_OPTIONS,
            selectedRole:  null,  // 'ISSUER' | 'RECEIVER' | null
            issuerForm:    makeIssuerForm(),
            receiverForm:  makeReceiverForm(),
            issuerFiles:   [],
            receiverFiles: [],
            website:       '',   // 허니팟(스팸 봇 차단) — 정상 사용자는 비움
            errors:        {},
            isSubmitting:  false,
            submitError:   '',   // 서버/네트워크 오류 안내 (§3.4)
        };
    },

    computed: {
        privacyHref() {
            // PrivacyPage 라우트는 '/:lang/privacy'로 lang 파라미터가 필수 — 누락 시 href가 깨진다.
            const lang = this.$route.params.lang || 'kor';
            return this.$router.resolve({ name: 'PrivacyPage', params: { lang } }).href;
        },
    },

    watch: {
        isOpen(open) {
            if (open) {
                this.$nextTick(() => {
                    if (this.$refs.scrollBody) this.$refs.scrollBody.scrollTop = 0;
                });
            } else {
                setTimeout(() => this._resetAll(), 300);
            }
        },
    },

    mounted() {
        this._onKeydown = e => { if (e.key === 'Escape' && this.isOpen) this.handleClose(); };
        document.addEventListener('keydown', this._onKeydown);
    },

    beforeDestroy() {
        document.removeEventListener('keydown', this._onKeydown);
    },

    methods: {
        // ── 역할 선택 ─────────────────────────────────────
        selectRole(role) {
            this.selectedRole = role;
            this.clearErr('role');
        },

        // ── 닫기 ──────────────────────────────────────────
        handleClose() {
            if (this.isSubmitting) return;
            this.$emit('close');
        },

        // ── 에러 초기화 ───────────────────────────────────
        clearErr(field) {
            if (this.errors[field]) this.$set(this.errors, field, '');
        },

        // 연락처 자동 하이픈 보정 (blur 시 호출) — 어드민과 동일 정책
        formatPhoneKR,

        // ── 유효성 검사 ───────────────────────────────────
        _validate() {
            const e = {};
            const EMAIL_RE = /^\S+@\S+\.\S+$/;

            if (!this.selectedRole) {
                e.role = '문의 대상(발행자/수령자)을 선택해 주세요.';
                this.errors = e;
                return false;
            }

            if (this.selectedRole === 'ISSUER') {
                if (!this.issuerForm.companyName) e.issuer_companyName = '회사명을 입력해 주세요.';
                if (!this.issuerForm.contactName) e.issuer_contactName = '이름을 입력해 주세요.';
                if (!this.issuerForm.phone)       e.issuer_phone       = '연락처를 입력해 주세요.';
                if (!this.issuerForm.email)       { e.issuer_email = '이메일을 입력해 주세요.'; }
                else if (!EMAIL_RE.test(this.issuerForm.email)) { e.issuer_email = '올바른 이메일 형식을 입력해 주세요.'; }
                if (!this.issuerForm.agreePrivacy) e.issuer_agree = '개인정보 수집 및 이용에 동의해 주세요.';

            } else {
                if (!this.receiverForm.contactName)   e.rcv_contactName   = '이름을 입력해 주세요.';
                if (!this.receiverForm.phone)         e.rcv_phone         = '연락처를 입력해 주세요.';
                if (!this.receiverForm.email)         { e.rcv_email = '이메일을 입력해 주세요.'; }
                else if (!EMAIL_RE.test(this.receiverForm.email)) { e.rcv_email = '올바른 이메일 형식을 입력해 주세요.'; }
                if (!this.receiverForm.publisherName) e.rcv_publisherName   = '발행처를 입력해 주세요.';
                if (!this.receiverForm.badgeName)     e.rcv_badgeName       = '배지명을 입력해 주세요.';
                if (!this.receiverForm.walletStatus)  e.rcv_walletStatus    = '지갑 생성 여부를 선택해 주세요.';
                if (!this.receiverForm.agreePrivacy)  e.rcv_agree           = '개인정보 수집 및 이용에 동의해 주세요.';
            }

            this.errors = e;
            return Object.keys(e).length === 0;
        },

        // ── 제출 (§3.3 / §3.4) ────────────────────────────
        async handleSubmit() {
            if (!this._validate()) {
                this.$nextTick(() => {
                    const el = this.$refs.scrollBody?.querySelector('.pid-field--err, .pid-role-wrap--err');
                    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
                });
                return;
            }

            // 활성 역할의 폼 데이터만 전송 (§3.3 — 1문의 = 1역할)
            const payload = this.selectedRole === 'ISSUER'
                ? { role: 'ISSUER', ...this.issuerForm, files: [...this.issuerFiles] }
                : { role: 'RECEIVER', ...this.receiverForm, files: [...this.receiverFiles] };
            payload.website = this.website;   // 허니팟 값 동봉(서버에서 봇 판정)

            this.submitError = '';
            this.isSubmitting = true;
            try {
                const res = await this.$store.dispatch('inquery/submitUsageInquiry', payload);

                // DB 저장 성공·비상 메일 성공 모두 접수 완료 동일 처리(§3.4)
                const message = res?.message || '문의가 접수되었습니다. 빠른 시일 내 연락드리겠습니다.';
                this.$emit('submitted', message);
            } catch (err) {
                // 접수 미완료 — 화면에 오류 안내 후 재시도 유도(§3.4)
                console.error('[플랫폼 문의 제출 오류]', err);
                this.submitError = resolveApiErrorMessage(err, '문의 접수 중 오류가 발생했습니다. 잠시 후 다시 시도해 주세요.');
            } finally {
                this.isSubmitting = false;
            }
        },

        // ── 전체 초기화 ───────────────────────────────────
        _resetAll() {
            this.selectedRole  = null;
            this.issuerForm    = makeIssuerForm();
            this.receiverForm  = makeReceiverForm();
            this.issuerFiles   = [];
            this.receiverFiles = [];
            this.errors        = {};
            this.submitError   = '';
            // ContactFileUpload의 내부 상태도 초기화
            this.$refs.issuerUpload?.reset();
            this.$refs.receiverUpload?.reset();
        },
    },
};
</script>

<style lang="scss" scoped>
@import './inquiry-tokens';

// ─── 로컬 별칭 ──────────────────────────────────────────────
$orange    : $inq-orange;
$blue-dark : $inq-blue-dark;
$border    : $inq-border-input;
$focus-bdr : $inq-focus-bdr;
$focus-shd : $inq-focus-shd;
$active-bdr: $inq-active-bdr;
$active-bg : $inq-active-bg;
$active-clr: $inq-active-clr;
$err-clr   : $inq-err-clr;
$panel-bg  : $inq-panel-bg;

// ─── 오버레이 ───────────────────────────────────────────────
.pid-backdrop {
    position:   fixed;
    inset:      0;
    background: $inq-backdrop;
    z-index:    9998;
    cursor:     pointer;
}
.pid-bg-enter-active,
.pid-bg-leave-active { transition: opacity .22s ease; }
.pid-bg-enter,
.pid-bg-leave-to     { opacity: 0; }

// ─── 패널 (오른쪽에서 슬라이드) ────────────────────────────
.pid-panel {
    position:       fixed;
    top:            0;
    right:          0;
    bottom:         0;
    width:          clamp(680px, 50vw, 840px);
    background:     $panel-bg;
    box-shadow:     -6px 0 48px $inq-shadow;
    z-index:        9999;
    display:        flex;
    flex-direction: column;
    transform:      translateX(105%);
    transition:     transform .28s cubic-bezier(.4, 0, .2, 1);

    &--open { transform: translateX(0); }
}

// ─── 헤더 ───────────────────────────────────────────────────
.pid-hd {
    flex-shrink:     0;
    display:         flex;
    justify-content: space-between;
    align-items:     flex-start;
    padding:         20px 40px 16px;
    border-bottom:   1px solid $inq-border-divider;
    background:      #fff;
}

// _common.scss 전역 h 태그에 color !important 가 있어 명시적으로 override
.pid-hd__title {
    font-size:   2.1rem;
    font-weight: 800;
    color:       $blue-dark !important;
    margin:      0 0 6px;
    line-height: 1.3;
}

.pid-hd__desc {
    font-size:   1.35rem;
    color:       $inq-text-sub;
    margin:      0;
    line-height: 1.6;
}

.pid-close-btn {
    flex-shrink:     0;
    width:           36px;
    height:          36px;
    border:          none;
    border-radius:   8px;
    background:      $inq-ctrl-bg;
    color:           #667085;
    cursor:          pointer;
    display:         flex;
    align-items:     center;
    justify-content: center;
    margin-top:      2px;
    transition:      background .15s ease, color .15s ease;
    &:hover { background: $inq-ctrl-hv; color: $inq-text-primary; }
}

// ─── 바디 ───────────────────────────────────────────────────
.pid-body {
    flex:       1 1 auto;
    overflow-y: auto;
    padding:    28px 40px 20px;
}

// ─── 섹션 ───────────────────────────────────────────────────
// gap 방식: 섹션 내부 요소 간격을 gap으로 관리 → 마지막 자식 뒤에 여백 누적 없음
.pid-sec {
    display:        flex;
    flex-direction: column;
    gap:            16px;
    margin-bottom:  36px;

    &--last { margin-bottom: 0; }
}

.pid-sec__title {
    display:     flex;
    align-items: center;
    gap:         10px;
    font-size:   1.7rem;
    font-weight: 800;
    color:       $inq-text-primary !important;
    margin:      0;

    &::before {
        content:       '';
        width:         5px;
        height:        18px;
        border-radius: 99px;
        background:    $orange;
        flex-shrink:   0;
    }
}

// ─── 필수 · 선택 뱃지 ───────────────────────────────────────
.pid-badge {
    display:        inline-flex;
    align-items:    center;
    padding:        2px 8px;
    border-radius:  99px;
    font-size:      1.1rem;
    font-weight:    700;
    line-height:    1;
    vertical-align: middle;

    &--req   { background: rgba(218, 30, 40, .08); color: #da1e28; }
    &--opt   { background: #f0f4f8;                color: #6b7280; }
}

// ─── 역할 선택 카드 ─────────────────────────────────────────
.pid-role-wrap {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap:     12px;

    &--err .pid-role-btn { border-color: $inq-focus-bdr; }

    @media (max-width: 540px) { grid-template-columns: 1fr; }
}

.pid-role-btn {
    display:     flex;
    flex-direction: row;
    align-items: center;
    gap:         12px;
    padding:     12px 16px;
    border:      1.5px solid $border;
    border-radius: 12px;
    background:  #fff;
    cursor:      pointer;
    text-align:  left;
    width:       100%;
    transition:  border-color .18s ease, background .18s ease, box-shadow .18s ease;

    &__icon {
        flex-shrink:     0;
        width:           36px;
        height:          36px;
        border-radius:   50%;
        background:      $inq-ctrl-bg;
        display:         flex;
        align-items:     center;
        justify-content: center;
        color:           #6b7a8d;
        transition:      background .18s ease, color .18s ease;
    }

    &__body {
        display:        flex;
        flex-direction: column;
        gap:            2px;
    }

    &__main {
        font-size:   1.5rem;
        font-weight: 800;
        color:       $inq-text-primary;
        line-height: 1.3;
    }

    &__sub {
        font-size:   1.2rem;
        color:       $inq-text-sub;
        line-height: 1.4;
    }

    &:hover:not(.pid-role-btn--on) {
        border-color: $inq-hover-bdr;
        background:   $inq-hover-bg;
    }

    &--on {
        border-color: $active-bdr;
        background:   $active-bg;
        box-shadow:   0 0 0 3px $inq-orange-08;

        .pid-role-btn__icon {
            background: $inq-orange-12;
            color:      $orange;
        }

        .pid-role-btn__main { color: $active-clr; }
    }
}

// ─── 2열 그리드 ─────────────────────────────────────────────
// margin-bottom 제거 — 부모 pid-sec의 gap이 간격을 담당
.pid-grid {
    display:               grid;
    grid-template-columns: 1fr 1fr;
    gap:                   16px;

    @media (max-width: 540px) { grid-template-columns: 1fr; }
}

// ─── 필드 ───────────────────────────────────────────────────
// margin-bottom 제거 — 부모(pid-sec 또는 pid-grid)의 gap이 간격을 담당
.pid-field {
    display:        flex;
    flex-direction: column;
    gap:            6px;

    // 그리드 내 전체 너비 (이메일 등)
    &--full {
        grid-column: 1 / -1;
    }

    &--err .pid-inp {
        border-color: $inq-err-bdr;
        background:   $inq-err-bg;
    }
}

.pid-lbl {
    font-size:   1.4rem;
    font-weight: 700;
    color:       $inq-text-label;
}

.pid-errmsg { font-size: 1.2rem; color: $err-clr; font-weight: 600; }

// ─── 입력 필드 ──────────────────────────────────────────────
.pid-inp {
    width:         100%;
    height:        48px;
    padding:       0 14px;
    border:        1px solid $border;
    border-radius: 7px;
    background:    #fff;
    font-size:     1.5rem;
    font-family:   inherit;
    color:         $inq-text-primary;
    outline:       none;
    transition:    border-color .18s ease, box-shadow .18s ease;

    &::placeholder { color: $inq-text-ph; }

    &:focus {
        border-color: $focus-bdr;
        box-shadow:   0 0 0 4px $focus-shd;
    }

    &:disabled {
        background: #f8fafb;
        color:      $inq-text-ph;
        cursor:     not-allowed;
    }

    &--ta {
        height:      auto;
        padding:     12px 14px;
        resize:      vertical;
        line-height: 1.6;
        min-height:  100px;
    }
}

// ─── 옵션 버튼 (지갑 생성 여부) ─────────────────────────────
.pid-opt-wrap {
    display:               grid;
    grid-template-columns: repeat(2, 1fr);
    gap:                   8px;
}

.pid-opt {
    display:      inline-flex;
    align-items:  center;
    gap:          8px;
    padding:      0 14px;
    height:       48px;
    border:       1px solid $border;
    border-radius: 7px;
    background:   #fff;
    font-size:    1.35rem;
    font-weight:  600;
    color:        $inq-text-label;
    cursor:       pointer;
    user-select:  none;
    text-align:   left;
    transition:   border-color .15s ease, background .15s ease, color .15s ease;

    &::before {
        content:       '';
        width:         16px;
        height:        16px;
        flex-shrink:   0;
        border:        1.5px solid $inq-opt-mark;
        background:    #fff;
        transition:    border-color .15s ease, background .15s ease;
    }

    &--radio::before { border-radius: 50%; }

    &--on {
        border-color: $active-bdr;
        background:   $active-bg;
        color:        $active-clr;
    }

    &--radio.pid-opt--on::before {
        border-color: $orange;
        background:   $orange;
        box-shadow:   inset 0 0 0 3px #fff;
    }

    &:hover:not(.pid-opt--on):not(:disabled) {
        border-color: $inq-hover-bdr;
        background:   $inq-hover-bg;
    }

    &:disabled { opacity: .5; cursor: not-allowed; }
}

// ─── 역할 미선택 안내 ────────────────────────────────────────
.pid-role-hint {
    display:     flex;
    align-items: center;
    gap:         12px;
    padding:     20px 24px;
    border:      1.5px dashed #d0d7e2;
    border-radius: 10px;
    background:  $inq-agree-bg;
    margin-top:  8px;
}

.pid-role-hint__ico {
    font-size:   22px;
    color:       $orange;
    flex-shrink: 0;
    line-height: 1;
}

.pid-role-hint__txt {
    font-size:   1.4rem;
    color:       $inq-text-sub;
    margin:      0;
    line-height: 1.5;
}

// ─── 개인정보 동의 ──────────────────────────────────────────
.pid-agree {
    display:     flex;
    align-items: center;
    gap:         12px;
    padding:     16px 20px;
    border:      1px solid $inq-border-divider;
    border-radius: 10px;
    background:  $inq-agree-bg;
}

.pid-agree__box {
    flex-shrink:     0;
    width:           22px;
    height:          22px;
    border:          1.5px solid $border;
    border-radius:   5px;
    background:      #fff;
    cursor:          pointer;
    display:         flex;
    align-items:     center;
    justify-content: center;
    transition:      border-color .15s ease, background .15s ease;

    &--on {
        border-color: $orange;
        background:   $orange;
    }

    &:disabled { opacity: .5; cursor: not-allowed; }
}

.pid-agree__txt {
    font-size:   1.4rem;
    color:       $inq-text-label;
    line-height: 1.5;
    margin:      0;
}

.pid-agree__link {
    color:           $inq-text-link;
    font-weight:     700;
    text-decoration: underline;
    &:hover { color: $inq-text-link-hv; }
}

// ─── 푸터 ───────────────────────────────────────────────────
.pid-ft {
    flex-shrink:  0;
    padding:      16px 40px;
    border-top:   1px solid $inq-border-divider;
    background:   #fff;
}

.pid-ft__hint {
    text-align:  center;
    font-size:   1.4rem;
    color:       $inq-text-sub;
    margin:      8px 0 0;
}

// 제출 실패 안내 (§3.4)
.pid-submit-err {
    margin:      0 0 10px;
    font-size:   1.3rem;
    font-weight: 600;
    color:       $inq-err-clr;
    text-align:  center;
}

.pid-submit {
    width:           100%;
    height:          52px;
    border:          none;
    border-radius:   10px;
    background:      $blue-dark;
    color:           #fff;
    font-size:       1.6rem;
    font-weight:     700;
    font-family:     inherit;
    cursor:          pointer;
    display:         flex;
    align-items:     center;
    justify-content: center;
    gap:             8px;
    transition:      background .18s ease, transform .1s ease, opacity .18s ease;

    &:hover:not(:disabled) { background: #162e6d; }
    &:active:not(:disabled) { transform: scale(.99); }

    &:disabled {
        background: #b8c0cc;
        cursor:     not-allowed;
        opacity:    .85;
    }
}

.pid-submit__spinner {
    width:            18px;
    height:           18px;
    border:           2.5px solid rgba(255, 255, 255, .3);
    border-top-color: #fff;
    border-radius:    50%;
    animation:        pid-spin .7s linear infinite;
}

@keyframes pid-spin { to { transform: rotate(360deg); } }

// ─── 반응형 ─────────────────────────────────────────────────
@media (max-width: 767px) {
    .pid-panel { width: 100%; min-width: unset; }
    .pid-hd    { padding: 16px 20px 12px; }
    .pid-body  { padding: 20px 20px 16px; }
    .pid-ft    { padding: 12px 20px; }
    .pid-sec   { gap: 12px; margin-bottom: 28px; }
    .pid-grid  { grid-template-columns: 1fr; }
    .pid-field--full { grid-column: 1; }
}
</style>
